import { MonitorCog, Table, FileText, Bot, BrainCircuit, Network, Code2, Wifi, Check, ArrowUpRight } from "lucide-react";
import { SERVICES } from "../data/content";
import { Reveal, SectionHeading } from "./Reveal";

const ICONS = {
  "it-support": MonitorCog,
  "excel-data": Table,
  "word-ppt": FileText,
  automation: Bot,
  "comp-problem-solving": BrainCircuit,
  "algorithm-design": Network,
  "software-dev": Code2,
  networking: Wifi,
};

const SPANS = {
  "it-support": "lg:col-span-2",
  "algorithm-design": "lg:col-span-2",
};

export default function Services({ onEnquire }) {
  return (
    <section id="services" className="relative py-20 md:py-28" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          overline="Independent services"
          title="Practical services, transparent starting prices"
          copy="Every engagement starts with a clear, fixed quote before any work begins. No hidden costs, no jargon — just dependable help from £20/hr."
        />

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.id];
            return (
              <Reveal key={s.id} delay={Math.min(i * 0.06, 0.3)} className={SPANS[s.id] || ""}>
                <article
                  data-testid={`service-card-${s.id}`}
                  className="group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white/[0.03] p-6 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.05] md:p-7"
                  style={{ borderColor: `${s.color}2e` }}
                >
                  <div
                    className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
                    style={{ background: `${s.color}26` }}
                  />
                  <div className="flex items-start justify-between">
                    <span
                      className="flex h-11 w-11 items-center justify-center rounded-xl"
                      style={{ background: `${s.color}14`, color: s.color }}
                    >
                      <Icon size={21} strokeWidth={1.8} />
                    </span>
                    <span
                      className="rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em]"
                      style={{ borderColor: `${s.color}45`, color: s.color }}
                    >
                      {s.badge}
                    </span>
                  </div>

                  <p className="mt-5 font-mono text-[11px] tracking-[0.25em] text-slate-500">{s.num}</p>
                  <h3 className="font-display mt-1 text-lg font-semibold text-slate-50">{s.title}</h3>
                  <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-400">{s.description}</p>

                  <ul className="mt-4 space-y-1.5">
                    {s.deliverables.slice(0, 3).map((d) => (
                      <li key={d} className="flex items-center gap-2 text-xs text-slate-300">
                        <Check size={13} style={{ color: s.color }} />
                        {d}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
                    <span className="font-mono text-sm font-semibold" style={{ color: s.color }} data-testid={`service-rate-${s.id}`}>
                      {s.rate}
                    </span>
                    <button
                      data-testid={`service-enquire-${s.id}`}
                      onClick={() => onEnquire(s.title)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 transition-colors hover:text-white"
                    >
                      Enquire
                      <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </div>
                </article>
              </Reveal>
            );
          })}

          <Reveal delay={0.2} className="lg:col-span-2">
            <div
              className="flex h-full flex-col justify-center rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 via-[#0D152D] to-blue-600/10 p-6 md:p-7"
              data-testid="services-cta-card"
            >
              <h3 className="font-display text-xl font-bold text-slate-50">Have a technical challenge or a process to improve?</h3>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-400">
                Let's explore a practical solution together. Describe the problem in plain English — you will get an
                honest assessment and a clear quote, with no obligation.
              </p>
              <button
                data-testid="services-cta-button"
                onClick={() => onEnquire("Something else / not sure")}
                className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-6 py-3 text-sm font-semibold text-[#04101F] shadow-[0_0_24px_rgba(0,240,255,0.35)] transition-transform duration-200 hover:scale-[1.03]"
              >
                Ask a question
                <ArrowUpRight size={15} />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
